import React, { useEffect, useState } from "react";
import { Circle, Layer, Line, Text } from "react-konva";
import checkForOverlap from "./overlapCheck.component";

export default function CustomMask(props) {
    const shapeRef = React.useRef();
    const layerRef = React.useRef();

    const [points, setPoints] = React.useState(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [initialPos, setInitialPos] = React.useState({ x: 0, y: 0 });
    const [overlapInfo, setOverlapInfo] = useState([]);
    const [activePointIndex, setActivePointIndex] = useState(null);

    const { mask, maskList, stageScale } = props;
    const shouldFlipY = Boolean(props.flipY && Number.isFinite(props.worldHeight));
    const toWorldY = (y) => (shouldFlipY ? (props.worldHeight - y) : y);
    const toDisplayY = (y) => (shouldFlipY ? (props.worldHeight - y) : y);
    const safeStageScale = (Number.isFinite(stageScale) && stageScale > 0) ? stageScale : 1;

    useEffect(() => {
        if (mask.points && Array.isArray(mask.points)) {
            const flatPoints = [];
            mask.points.slice(0, -1).forEach(point => {
                if (point && typeof point.x === "number" && typeof point.y === "number") {
                    flatPoints.push(point.x, toDisplayY(point.y));
                } else {
                    console.log("Invalid point:", point);
                }
            });
            setPoints(flatPoints);
        } else {
            console.log("Invalid mask points:", mask.points);
        }
    }, [mask.points, shouldFlipY, props.worldHeight]);


    var selected = mask.selected;
    const color = mask.color || "lightblue";
    // Match grid-like visual weight so mask borders don't overpower the drawing.
    const strokeWidth = Math.max(0.01, 0.12 / safeStageScale);
    // Keep polygon edit handles close to transformer-anchor feel.
    const circleRadius = Math.min(0.55, Math.max(0.04, 0.34 / safeStageScale));
    const handleStrokeWidth = Math.max(0.01, 0.08 / safeStageScale);
    const handleHitWidth = Math.max(circleRadius * 1.25, 0.875 / safeStageScale);
    const handleActivationRadius = Math.max(circleRadius * 0.875, 0.55 / safeStageScale);
    const vertexPoints = Array.isArray(points)
        ? Array.from({ length: Math.floor(points.length / 2) }, (_, index) => ({
            index,
            x: points[index * 2],
            y: points[index * 2 + 1],
        }))
        : [];

    const getNearestPointIndex = (pointer, preferredIndex = 0) => {
        if (!pointer || !shapeRef.current || vertexPoints.length === 0) return preferredIndex;

        const lineX = shapeRef.current.x();
        const lineY = shapeRef.current.y();
        let nearestIndex = preferredIndex;
        let nearestDistance = Number.POSITIVE_INFINITY;

        vertexPoints.forEach((point) => {
            const dx = pointer.x - (point.x + lineX);
            const dy = pointer.y - (point.y + lineY);
            const distance = Math.hypot(dx, dy);

            if (distance <= handleActivationRadius && distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = point.index;
            }
        });

        return nearestIndex;
    };

    const handleCircleDragStart = (e, index) => {
        const pointer = e.target.getStage()?.getPointerPosition();
        const targetIndex = getNearestPointIndex(pointer, index);
        setActivePointIndex(targetIndex);
    };

    const handleLineDragStart = (e) => {
        setIsDragging(true);
        setInitialPos({ x: e.target.x(), y: e.target.y() });
    };

    const handleLineDragEnd = (e) => {
        setIsDragging(false);
        const dx = e.target.x() - initialPos.x;
        const dy = e.target.y() - initialPos.y;

        const updatedPoints = points.map((coord, index) =>
            index % 2 === 0 ? coord + dx : coord + dy
        );

        setPoints(updatedPoints);

        const xyPoints = [];
        for (let i = 0; i < updatedPoints.length; i += 2) {
            xyPoints.push({ x: updatedPoints[i], y: toWorldY(updatedPoints[i + 1]) });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }

        props.updateMask(null, props.i, "polygon", xyPoints);
        e.target.position({ x: 0, y: 0 });
    };

    const handleCircleDragMove = (e, index) => {
        if (!shapeRef.current) return;
        const lineX = shapeRef.current.x();
        const lineY = shapeRef.current.y();
        const targetIndex = activePointIndex ?? index;

        const newPoints = [...points];
        newPoints[targetIndex * 2] = e.target.x() - lineX;
        newPoints[targetIndex * 2 + 1] = e.target.y() - lineY;
        setPoints(newPoints);

        const xyPoints = [];
        for (let i = 0; i < newPoints.length; i += 2) {
            xyPoints.push({ x: newPoints[i], y: toWorldY(newPoints[i + 1]) });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }
    };

    const handleCircleDragEnd = (e, index) => {
        const lineX = shapeRef.current.x();
        const lineY = shapeRef.current.y();
        const targetIndex = activePointIndex ?? index;
        const newPoints = [...points];
        newPoints[targetIndex * 2] = e.target.x() - lineX;
        newPoints[targetIndex * 2 + 1] = e.target.y() - lineY;
        setPoints(newPoints);

        const xyPoints = [];
        for (let i = 0; i < newPoints.length; i += 2) {
            xyPoints.push({ x: newPoints[i], y: toWorldY(newPoints[i + 1]) });
        }

        if (xyPoints.length > 0) {
            xyPoints.push({ ...xyPoints[0] });
        }

        props.updateMask(null, props.i, "polygon", xyPoints);
        setActivePointIndex(null);
    };


    const handleContextMenu = (e, maskIndex, pointIndex) => {
        console.log("handleContextMenu", maskIndex, pointIndex)
        props.handleContextMenu(e, maskIndex, pointIndex)
    }

    const getDynamicFontSize = () => {
        const safeScale = Math.max(0.1, Number(props.stageScale) || 1);
        return Math.max(0.22, 2.6 / safeScale);
    };
    const textFlipProps = props.textUnflipY
        ? { scaleY: -1, offsetY: getDynamicFontSize() }
        : {};

    const handleSelect = React.useCallback(() => {
        props.selectMask(mask.id);
    }, [props.selectMask, mask.id, maskList]);

    const getTextPosition = () => {
        const dynamicFontSize = getDynamicFontSize();
        const inset = Math.max(0.18, dynamicFontSize * 0.18);
        const handleClearance = Math.max(circleRadius * 1.8, dynamicFontSize * 0.8);

        if (!Array.isArray(vertexPoints) || vertexPoints.length === 0) {
            return { x: 0, y: 0 };
        }

        const topPoint = vertexPoints.reduce((best, point) => {
            if (!best) return point;
            if (props.textUnflipY) {
                if (point.y > best.y) return point;
                if (point.y === best.y && point.x < best.x) return point;
                return best;
            }
            if (point.y < best.y) return point;
            if (point.y === best.y && point.x < best.x) return point;
            return best;
        }, null);

        if (!topPoint || topPoint.x === undefined || topPoint.y === undefined) {
            return { x: 0, y: 0 };
        }

        return {
            x: topPoint.x + inset,
            y: props.textUnflipY
                ? (topPoint.y + handleClearance - dynamicFontSize)
                : (topPoint.y - dynamicFontSize - handleClearance),
        };
    };

    const textPosition = getTextPosition();


    useEffect(() => {
        const overlapInfo = checkForOverlap(props.maskList);
        setOverlapInfo(overlapInfo || []);
    }, [props.maskList]);

    const sortPointsClockwise = (overlap) => {
        const points = overlap.overlapPoints;
        if (points.length < 3) return points;
        if (overlap.mask1Type === "polygon" || overlap.mask2Type === "polygon") {
            // console.log("calling sortPolygonPoints", points);
            const polyPoints = sortPolygonPoints(points);
            if (polyPoints.length > 0) return polyPoints;
        }

        const centroid = points.reduce(
            (acc, p) => ({ x: acc.x + p.x / points.length, y: acc.y + p.y / points.length }),
            { x: 0, y: 0 }
        );

        const uniquePoints = Array.from(
            new Map(points.map(p => [`${p.x.toFixed(6)},${p.y.toFixed(6)}`, p])).values()
        );

        return uniquePoints.sort((a, b) => {
            const angleA = Math.atan2(a.y - centroid.y, a.x - centroid.x);
            const angleB = Math.atan2(b.y - centroid.y, b.x - centroid.x);
            return angleA - angleB;
        });
    };

    const sortPolygonPoints = (points) => {
        if (points.length < 3) return points;

        const round = (num) => Number(num.toFixed(4));
        const format = (point) => `${round(point.x)},${round(point.y)}`;

        const overlapSet = new Set(points.map(format));

        let polygonMask = null;
        let maxMatches = 0;
        const nonPolygonMasks = [];

        for (const mask of props.maskList) {
            if (!Array.isArray(mask.points)) continue;

            if (mask.type === "polygon") {
                const matchCount = mask.points.reduce((count, pt) =>
                    overlapSet.has(format(pt)) ? count + 1 : count
                    , 0);

                if (matchCount > maxMatches) {
                    polygonMask = mask;
                    maxMatches = matchCount;
                }
            } else {
                nonPolygonMasks.push(mask);
            }
        }

        if (!polygonMask) {
            return [];
        }

        return fitPointsIntoPolygon(polygonMask.points, points, nonPolygonMasks);
    };

    function fitPointsIntoPolygon(polygonPoints, unorderedPoints, nonPolygonMasks) {
        const round = (num) => Number(num.toFixed(4));
        const format = (pt) => `${round(pt.x)},${round(pt.y)}`;
        const unorderedSet = new Set(unorderedPoints.map(format));

        const isPointOnSegment = (p, a, b, tolerance = 1.5) => {
            const cross = Math.abs((b.y - a.y) * (p.x - a.x) - (b.x - a.x) * (p.y - a.y));
            if (cross > tolerance) return false;

            const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
            if (dot < 0) return false;

            const lenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
            if (dot > lenSq) return false;

            return true;
        };

        let corners = [];
        nonPolygonMasks.forEach(mask => {
            mask.points.forEach(point => {
                if (unorderedPoints.some(p => p.x === point.x && p.y === point.y)) {
                    corners.push(point);
                }
            });
        });

        const pointsToFit = unorderedPoints.slice();

        let result = [];

        for (let i = 0; i < polygonPoints.length - 1; i++) {
            const a = polygonPoints[i];
            const b = polygonPoints[i + 1];

            result.push(a);

            const pointsOnSegment = [];

            for (let j = pointsToFit.length - 1; j >= 0; j--) {
                const p = pointsToFit[j];
                if (isPointOnSegment(p, a, b)) {
                    pointsOnSegment.push(p);
                    pointsToFit.splice(j, 1);
                }
            }

            pointsOnSegment.sort((p1, p2) => {
                const d1 = Math.hypot(p1.x - a.x, p1.y - a.y);
                const d2 = Math.hypot(p2.x - a.x, p2.y - a.y);
                return d1 - d2;
            });

            result.push(...pointsOnSegment);
        }

        corners.forEach(corner => {
            const key = format(corner);
            if (result.some(pt => format(pt) === key)) return;

            let bestIndex = -1;
            let bestDist = Infinity;

            for (let i = 0; i < result.length - 1; i++) {
                const a = result[i];
                const b = result[i + 1];
                const dist = pointToSegmentDistance(corner, a, b);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIndex = i + 1;
                }
            }

            if (bestIndex >= 0) {
                result.splice(bestIndex, 0, corner);
            } else {
                result.push(corner);
            }
        });

        result = result.filter(pt => unorderedSet.has(format(pt)));
        result = removeDuplicatePoints(result);

        return result;

        function pointToSegmentDistance(p, a, b) {
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);

            const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
            const clampedT = Math.max(0, Math.min(1, t));
            const projX = a.x + clampedT * dx;
            const projY = a.y + clampedT * dy;
            return Math.hypot(p.x - projX, p.y - projY);
        }

        function removeDuplicatePoints(pts) {
            const seen = new Set();
            const out = [];
            for (const pt of pts) {
                const key = format(pt);
                if (!seen.has(key)) {
                    seen.add(key);
                    out.push(pt);
                }
            }
            return out;
        }
    }

    return (
        <Layer key={props.i} ref={layerRef}>
            {mask ? (
                <Line
                    ref={shapeRef}
                    points={points}
                    fill={color}
                    stroke={mask.polyStroke}
                    strokeWidth={strokeWidth}
                    lineCap="round"
                    lineJoin="round"
                    opacity={mask.opacity}
                    closed={mask.polyClosed}
                    draggable={selected}
                    listening={!mask.locked}
                    onDragStart={(e) => handleLineDragStart(e)}
                    onDragEnd={(e) => handleLineDragEnd(e)}
                    onClick={handleSelect} onTap={handleSelect} />
            ) : null}

            {!isDragging &&
                overlapInfo
                    .filter(
                        (overlap) =>
                            overlap.mask1Id === props.mask.id || overlap.mask2Id === props.mask.id
                    )
                    .map((overlap, index) => {
                        const sortedPoints = sortPointsClockwise(overlap);
                        const displayPoints = shouldFlipY
                            ? sortedPoints.map(p => ({ x: p.x, y: toDisplayY(p.y) }))
                            : sortedPoints;
                        return sortedPoints.length > 2 ? (
                            <Line
                                key={index}
                                points={displayPoints.flatMap(p => [p.x, p.y])}
                                fill="black"
                                closed
                                listening={false}
                                globalCompositeOperation="destination-out"
                            />
                        ) : null;
                    })}

            {vertexPoints.length > 0 && !isDragging && selected &&
                vertexPoints.map((point) => {
                    if (Number.isFinite(point.x) && Number.isFinite(point.y) && shapeRef.current) {
                        return (
                            <Circle
                                key={point.index}
                                x={point.x + shapeRef.current.x()}
                                y={point.y + shapeRef.current.y()}
                                radius={circleRadius}
                                fill="white"
                                stroke="black"
                                strokeWidth={handleStrokeWidth}
                                hitStrokeWidth={handleHitWidth}
                                draggable
                                onDragStart={(e) => handleCircleDragStart(e, point.index)}
                                onMouseDown={(e) => handleCircleDragStart(e, point.index)}
                                onTouchStart={(e) => handleCircleDragStart(e, point.index)}
                                onDragMove={(e) => handleCircleDragMove(e, point.index)}
                                onDragEnd={(e) => handleCircleDragEnd(e, point.index)}
                                onContextMenu={(e) => handleContextMenu(e, props.i, point.index)}
                            />
                        );
                    }
                    return null;
                })
            }


            {props.mask.name && !props.mask.visible && (
                <Text
                    text={props.mask.name}
                    x={textPosition.x}
                    y={textPosition.y}
                    align="left"
                    fontSize={getDynamicFontSize()}
                    fontStyle="bold"
                    fill="black"
                    {...textFlipProps}
                />
            )}
        </Layer>
    );
}
